import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import MailIcon from "@mui/icons-material/Mail";
import { useLocation } from "react-router";
import axios from "axios";
import { API_URL } from "../Endpoints";
import { CircularProgress, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";

const areDropdownOptionsEqual = (left, right) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every(
    (item, index) =>
      item.key === right[index].key && item.value === right[index].value,
  );
};

export default function ViewStacks(props) {
  const [open, setOpen] = React.useState(false);
  const [stacks, setStacks] = React.useState([]);
  const [dropdownOptions, setDropdownOptions] = React.useState([]);
  const dicom_data = useSelector((state) => state.dcm);
  const [loading, setLoading] = React.useState(true);
  const anchor = "left";
  const toggleDrawer = () => {
    setOpen(!open);
  };
  let { search } = useLocation();
  const dispatch = useDispatch();
  const [selectedKey, setSelectedKey] = React.useState("");
  const query = React.useMemo(() => new URLSearchParams(search), [search]);
  const stackId = query.get("stack_id");
  const role = query.get("role");
  const adjudicatorUserId = query.get("user_id");
  const userGradings = dicom_data?.formDataUserGrading?.data;
  const isGraderListLoading =
    role === "adjudicator" && !dicom_data?.formDataUserGrading?.ready;

  const handleChange = (event) => {
    const selectedAnnotationUserId = String(event.target.value);
    if (selectedKey === selectedAnnotationUserId) {
      return;
    }

    const selectedOption = dropdownOptions.find(
      (option) => option.key === selectedAnnotationUserId,
    );

    setSelectedKey(selectedAnnotationUserId);
    localStorage.setItem(stackId, selectedAnnotationUserId);

    if (props.onAnnotationSourceChange) {
      props.onAnnotationSourceChange(
        selectedAnnotationUserId,
        selectedOption?.value || "Annotations",
      );
    }
  };

  React.useEffect(() => {
    if (!userGradings) {
      return;
    }

    const keysDropdown = Object.keys(userGradings);
    const nextOptions = keysDropdown.map((key) => ({
      key: String(key),
      value: `${userGradings[key].grader_name} (grader)`,
    }));

    if (role === "adjudicator") {
      nextOptions.push({
        key: String(adjudicatorUserId),
        value: "Adjudicator",
      });
    }

    setDropdownOptions((currentOptions) =>
      areDropdownOptionsEqual(currentOptions, nextOptions)
        ? currentOptions
        : nextOptions,
    );
  }, [userGradings, role, adjudicatorUserId]);

  React.useEffect(() => {
    if (role !== "adjudicator") {
      return;
    }

    const storedUserId = localStorage.getItem(stackId);
    const nextSelectedKey = String(
      storedUserId || dicom_data?.fetchedId || adjudicatorUserId || "",
    );

    if (!nextSelectedKey) {
      return;
    }

    setSelectedKey((currentKey) =>
      currentKey === nextSelectedKey ? currentKey : nextSelectedKey,
    );
  }, [role, stackId, dicom_data?.fetchedId, adjudicatorUserId]);

  React.useEffect(() => {
    axios
      .get(`${API_URL}/api/v1/stacks?id=${stackId}&series=${query.get("series_no")}`)
      .then((res) => {
        setStacks(res.data);
        setLoading(false);

        dispatch({
          type: "NO_OF_STACKS",
          stacks: res.data,
        });
      })
      .catch(() => {
        setLoading(false);
      });
  }, [dispatch, query, stackId]);

  const onSeriesClicK = (series_id) => {
    window.location.assign(
      `/viewer?stack_id=${stackId}&user_id=${adjudicatorUserId}&series_no=${series_id}&role=${role}`,
    );
  };

  const list = (anchor) => (
    <Box
      sx={{ width: anchor === "top" || anchor === "bottom" ? "auto" : 250 }}
      role="presentation"
      onClick={toggleDrawer}
      onKeyDown={toggleDrawer}
    >
      <Typography variant="h6" sx={{ px: 2, py: 1 }}>
        Stack Series
      </Typography>
      <Divider />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {stacks?.map((stack, index) => (
            <ListItem key={stack.id} disablePadding>
              <ListItemButton
                selected={stack.id === query.get("series_no")}
                onClick={() => onSeriesClicK(stack.id)}
              >
                <ListItemIcon>
                  {index % 2 === 0 ? <InboxIcon /> : <MailIcon />}
                </ListItemIcon>
                <ListItemText primary={stack.id} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  const selectValue =
    selectedKey ||
    String(dicom_data?.fetchedId || adjudicatorUserId || "");

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          marginTop: "5px",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "left", ml: 1, mt: 0.7 }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={toggleDrawer}
          >
            {"View Stacks"}
          </Button>
        </Box>
        {role === "adjudicator" ? (
          <Box sx={{ display: "flex", justifyContent: "left", ml: 1, mt: 0.7 }}>
            {isGraderListLoading ? (
              <FormControl sx={{ width: "245px" }} size="small">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: "40px",
                    px: 1,
                  }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading graders...
                  </Typography>
                </Box>
              </FormControl>
            ) : dropdownOptions.length > 0 ? (
              <FormControl sx={{ width: "245px" }} size="small">
                <InputLabel id="annotation-source-label" shrink>
                  Load annotations from
                </InputLabel>
                <Select
                  labelId="annotation-source-label"
                  id="annotation-source-select"
                  label="Load annotations from"
                  value={selectValue}
                  onChange={handleChange}
                  disabled={props.annotationSourceLoading}
                  notched
                >
                  {dropdownOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </Box>
        ) : null}
      </div>
      <Drawer anchor={"left"} open={open} onClose={toggleDrawer}>
        {list(anchor)}
      </Drawer>
    </>
  );
}
